import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { useI18n } from "../i18n";
import { SERVICES } from "../data/services";
import { waProps, waLabel } from "../lib/whatsapp";

/**
 * ServicesSidebar — panel lateral sticky con la lista completa de servicios.
 * Estilo cromado / futurista, cada ítem tiene:
 *   • número monoespaciado (01., 02., ...)
 *   • barra cian animada que crece al hover
 *   • flecha esquina superior derecha que aparece
 *   • el título cambia a cian con un ligero desplazamiento horizontal
 * Al final, un CTA WhatsApp en verde-cian.
 *
 * Se oculta en <lg (el grid principal ya cubre 100% del ancho ahí).
 */
export default function ServicesSidebar({ whatsapp }) {
  const { pick } = useI18n();
  const wa = (whatsapp || "").replace(/\D/g, "");
  const waHref = wa
    ? `https://wa.me/${wa}?text=${encodeURIComponent(pick("Hola, quiero información sobre los servicios de XEGA.", "Hi, I want info about XEGA services.", "Olá, quero informação sobre os serviços da XEGA."))}`
    : "#contact";

  return (
    <aside
      className="hidden lg:block sticky top-28 self-start"
      data-testid="services-sidebar"
      aria-label={pick("Índice de servicios", "Services index", "Índice de serviços")}
    >
      {/* Chrome bezel frame */}
      <div
        className="p-[2px] rounded-2xl shadow-[0_18px_40px_-20px_rgba(0,0,0,0.9),0_0_50px_-15px_rgba(0,240,255,0.35)]"
        style={{
          background:
            'linear-gradient(135deg, #cfd6df 0%, #f4f7fa 12%, #8a93a1 30%, #2b313b 50%, #8a93a1 70%, #f4f7fa 88%, #cfd6df 100%)',
        }}
      >
        <div className="relative rounded-[14px] overflow-hidden bg-gradient-to-b from-[#0b1220] via-[#070a13] to-[#050810] p-5">
          {/* Ambient corner glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.35) 0%, transparent 65%)', filter: 'blur(6px)' }}
          />

          {/* Header */}
          <div className="relative flex items-center gap-2 mb-1">
            <span className="pulse-dot" />
            <span className="label-mono !text-cyan-300 !tracking-[0.35em] text-[10px]">
              {pick("ÍNDICE · CATÁLOGO", "INDEX · CATALOG", "ÍNDICE · CATÁLOGO")}
            </span>
          </div>
          <h3 className="relative font-display text-white text-xl leading-tight mb-4">
            <span className="chrome-text">{pick("Todos los servicios", "All services", "Todos os serviços")}</span>
          </h3>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-white/15 to-transparent mb-3" />

          {/* Service list */}
          <ul className="relative space-y-1">
            {SERVICES.map((s, i) => (
              <li key={s.slug}>
                <Link
                  to={`/servicios/${s.slug}`}
                  data-testid={`sidebar-service-${i}`}
                  className="group relative flex items-center gap-3 px-2 py-2 rounded-lg overflow-hidden transition-colors duration-300 hover:bg-white/5"
                >
                  {/* Sliding cyan indicator on the left edge */}
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-0 bg-gradient-to-b from-cyan-300 to-emerald-400 rounded-r-full transition-all duration-500 group-hover:h-3/4"
                    style={{ boxShadow: '0 0 12px rgba(0,240,255,0.75)' }}
                  />
                  {/* Number */}
                  <span className="font-mono-x text-[11px] text-white/40 group-hover:text-cyan-300 transition-colors duration-300 shrink-0 w-6">
                    {String(i + 1).padStart(2, "0")}.
                  </span>
                  {/* Title */}
                  <span className="font-tech text-sm text-white/85 group-hover:text-cyan-100 transition-all duration-300 group-hover:translate-x-0.5 tracking-tight truncate">
                    {pick(s.title_es, s.title_en, s.title_pt)}
                  </span>
                  {/* Arrow badge */}
                  <span className="ml-auto shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                    <ArrowUpRight className="w-4 h-4 text-cyan-300" style={{ filter: 'drop-shadow(0 0 4px rgba(0,240,255,0.6))' }} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-white/15 to-transparent my-4" />

          {/* WhatsApp CTA */}
          <a
            {...waProps(waHref)}
            data-testid="sidebar-whatsapp-cta"
            className="relative group/wa flex items-center justify-between gap-3 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-[#0d3f29] via-[#137a3b] to-[#10ff80]/30 border border-emerald-400/40 hover:border-emerald-300/80 transition overflow-hidden"
          >
            <span aria-hidden="true" className="absolute inset-0 opacity-70" style={{
              background: 'radial-gradient(circle at 80% 50%, rgba(16,255,128,0.35) 0%, transparent 60%)',
            }} />
            <span className="relative flex items-center gap-2.5">
              <MessageCircle className="w-4 h-4 text-emerald-300 group-hover/wa:scale-110 transition-transform" />
              <span className="font-tech text-sm text-white">
                {waLabel(pick, "Cotiza por WhatsApp", "Quote via WhatsApp", "Cotação por WhatsApp")}
              </span>
            </span>
            <ArrowUpRight className="relative w-4 h-4 text-emerald-100 group-hover/wa:text-white transition" />
          </a>
        </div>
      </div>
    </aside>
  );
}
